import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Modal from '../newsletter/Modal';
import Newsletter from '../newsletter/Newsletter';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 5;
`;

const Ul = styled.ul`
  list-style: none;
  display: flex;
  z-index: 10;

  li {
    position: relative;
    margin-right: 20px;
    margin-left: 20px;
    padding-top: 15px;
    padding-bottom: 15px;
    font-family: 'MedievalSharp', serif;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: bold;
    color: #8a8aff;
    cursor: pointer;

    &:hover {
      color: rgba(105, 146, 252, 1);
    }
  }

  .submenu {
    list-style: none;
    display: none;
    position: absolute;
    top: 100%;
    white-space: nowrap;
    background: #8a8aff;

    li {
      margin: 0;
      padding: 10px;
      font-size: clamp(1.1rem, 3vw, 1.5rem);
      color: white;

      &:hover {
        background-color: rgba(105, 146, 252, 1);
      }
    }
  }

  @media (min-width: 1450px) {
    ${({ open }) =>
      !open &&
      `
      .has-submenu:hover .submenu {
        display: block;
      }
    `}

    .newsletter {
      color: white;
      background-color: #8a8aff;
      padding: 10px;
      padding-top: 15px;
      border-radius: 10px;

      &:hover {
        color: white;
        background-color: rgba(105, 146, 252, 1);
      }
    }
  }

  @media (max-width: 1450px) {
    transform: ${({ open }) => open ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease-in-out;
    flex-flow: column nowrap;
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: clamp(280px, 90vw, 450px);
    padding-top: 115px;
    background-color: #4482d9ff;

    li {
      padding: 15px;
      border-bottom: 1px solid white;
      color: white;

      &:hover {
        color: white;
        background-color: #519fdfff;
      }
    }

    .has-submenu > div {
      display: flex;
      justify-content: space-between;
    }

    .submenu {
      position: static;
      background: none;
    }

    .submenu.show {
      display: block;
    }

    .submenu li {
      border-bottom: none;

      &:hover {
        background-color: #4482d9ff;
      }
    }
  }
`;

const StyledLink = styled(NavLink)`
  color: inherit;
  text-decoration: none;
  display: block;
  width: 100%;
  height: 100%;

  @media (min-width: 1450px) {
    &.active {
      ${({ $disableActive }) =>
        !$disableActive &&
        `
        text-decoration: underline;
        text-underline-offset: 10px;
        text-decoration-thickness: 3px;
      `}
    }
  }
`;

const BooksLabel = styled.span`
  @media (min-width: 1450px) {
    ${({ $active }) =>
      $active &&
      `
      text-decoration: underline;
      text-underline-offset: 10px;
      text-decoration-thickness: 3px;
    `}
  }
`;

const ArrowHead = styled.span`
  font-size: 1.5rem;
  margin-left: 10px;
  display: inline-block;
  transform: scale(1.4, 0.6);
  transform-origin: center;
`;

const RightNav = ({ open, setOpen }) => {
  const location = useLocation();
  const isBooksActive =
    location.pathname === '/beyond-prejudice' ||
    location.pathname === '/when-the-butterflies-are-gone';
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;

      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
  }, [open]);

  useEffect(() => {
  if (open) {
    setSubmenuOpen(false);
    }
  }, [open]);

  const handleBooksClick = (e) => {
    if (!open) return;

    e.preventDefault();
    setSubmenuOpen((prev) => !prev);
  };

  const handleLinkClick = () => {
    if (open) {
      setOpen(false);
      setSubmenuOpen(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1450 && open) {
        setOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, setOpen]);

  return (
    <>

      {open && <Overlay onClick={() => setOpen(false)} />}

      <Ul open={open} submenuOpen={submenuOpen}>

        <li>
          <StyledLink to="/" onClick={handleLinkClick}>Home</StyledLink>
        </li>

        <li className="has-submenu">

          <div 
            onClick={handleBooksClick}
            style={{ cursor: open ? "pointer" : "default" }}
          >
            <BooksLabel $active={isBooksActive}>Books</BooksLabel>
            <ArrowHead>
              {open ? (submenuOpen ? 'ᐱ' : 'ᐯ') : 'ᐯ'}
            </ArrowHead>
          </div>
         
          <ul className={`submenu ${submenuOpen ? 'show' : ''}`}>
            <li>
              <StyledLink to="/beyond-prejudice" onClick={handleLinkClick} $disableActive>Beyond Prejudice</StyledLink>
            </li>
            <li>
              <StyledLink to="/when-the-butterflies-are-gone" onClick={handleLinkClick} $disableActive>When The Butterflies Are Gone</StyledLink>
            </li>
          </ul>

        </li>

        <li>
          <StyledLink to="/about" onClick={handleLinkClick}>About</StyledLink>
        </li>

        <li>
          <StyledLink to="/contact" onClick={handleLinkClick}>Contact</StyledLink>
        </li>

        <li className="newsletter" onClick={() => setShowNewsletter(true)}>Newsletter</li>

      </Ul>

      {showNewsletter && (
        <Modal onClose={() => setShowNewsletter(false)}>
          <Newsletter />
        </Modal>
      )}

    </>
  );
};

export default RightNav;
